import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import type { ItemPatch, PlateItem } from "../api/backlog";
import type { RosterMember } from "../api/roster";
import { HORIZON_CHOICE, isHorizon } from "../horizons";
import { memberLabel } from "../members";

/**
 * **Edit** — correcting one thing on the family's list (issue #310).
 *
 * The canvas's middle act, and its words are what this form has to be true to: *"Boone got it
 * slightly wrong. The words you said stay underneath, unchanged."* Both halves are real. The
 * fields below are the ones Boone filled in *about* what was said; `ITEM.rawText` is what was
 * said, it is immutable by #134, and `update` cannot touch it — which is why deleting is a
 * separate act rather than a heavier edit.
 *
 * ## Four fields, and they are exactly what the row shows
 *
 * A card carries a title, whose it is and when it is due, under a heading that is its horizon.
 * So the form offers those four and nothing else, which keeps the screen's promise narrow: a
 * family can correct anything Boone put in front of them, and is never asked about a field
 * they have not been shown.
 *
 * **`priority` is the one `update` accepts and this does not** — see {@link ItemPatch}. It is
 * not on the row, so a control for it would be the only evidence a family had that it exists,
 * and #310 rules out anything that makes this page a ranking somebody maintains.
 *
 * ## An absent field means "leave it alone", and clearing is explicit
 *
 * #134's encoding, and the form is built around it: only what changed is sent, and taking a
 * due date or an owner **off** goes in `clear` rather than as an empty string. A form that
 * posted every field on every save would rewrite three things a family did not touch, and
 * `update`'s audit line would record all three as changes.
 *
 * ## Taking the owner off is a medium-risk write and this form does not say so
 *
 * Reassigning — or clearing — an owner asks first (ADR 0006 decision 2), and the ask is the
 * server's `message` in the confirmation. Nothing here predicts it: the screen sends the patch
 * and renders whichever envelope came back, which is `Memory.tsx`'s rule and the reason a
 * panel never becomes a second copy of `confirmation.ts`.
 */
export function ItemForm({
  item,
  members,
  pending,
  locked,
  error,
  onSubmit,
  onCancel,
}: {
  item: PlateItem;
  members: readonly RosterMember[];
  pending: boolean;
  /**
   * A confirmation is open on the page behind this form.
   *
   * **Separate from {@link pending}, and the gap between them is the bug it closes** (PR #330
   * review, on `FactForm`): `pending` is false while the dialog waits for an answer, so *Save
   * the change* would stay live behind the scrim and Shift+Tab out of the dialog could reach
   * it.
   */
  locked: boolean;
  error: string | null;
  onSubmit: (patch: ItemPatch) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(item.title);
  const [horizon, setHorizon] = useState(item.timeHorizon);
  const [due, setDue] = useState(item.dueDate ?? "");
  // `""` is *nobody* rather than "unanswered", which is the difference from `FactForm`: an item
  // with no owner is an ordinary, common state and not the absence #92 was about. A departed
  // owner's id matches no option, so a controlled `<select>` would render blank — see
  // {@link initialOwner}, which drops it to *Nobody* for the same reason the row shows no owner.
  const [owner, setOwner] = useState(initialOwner(item, members));

  const titleId = useId();
  const horizonId = useId();
  const dueId = useId();
  const ownerId = useId();
  const firstFieldRef = useRef<HTMLInputElement>(null);

  /**
   * Opening the form moves focus into it — the trigger unmounts when the form appears, so a
   * keyboard user was otherwise dumped to `<body>` and had to Tab back down the page.
   */
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  const trimmedTitle = title.trim();
  const patch = buildPatch(item, {
    title: trimmedTitle,
    horizon,
    due: due.trim(),
    owner,
    members,
  });
  const changed = Object.keys(patch).length > 0;
  const ready = trimmedTitle.length > 0 && changed;
  const frozen = pending || locked;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready || frozen) return;
    onSubmit(patch);
  }

  return (
    <form className="factform" onSubmit={submit}>
      <div className="field">
        <label className="field__label" htmlFor={titleId}>
          What is it?
        </label>
        <input
          className="field__input"
          id={titleId}
          ref={firstFieldRef}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          disabled={frozen}
          /* Matches `INPUT_LIMITS.title`. Capping an `<input>` is safe where capping a textarea
             is not: a title is a short label somebody types, not a pasted passage. */
          maxLength={TITLE_LIMIT}
          autoComplete="off"
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor={ownerId}>
          Who's down for it?
        </label>
        <select
          className="field__input"
          id={ownerId}
          value={owner}
          onChange={(event) => setOwner(event.target.value)}
          disabled={frozen}
        >
          <option value="">Nobody yet</option>
          {members.map((member) => (
            <option key={member.userId} value={member.userId}>
              {memberLabel(member, members)}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={horizonId}>
          When?
        </label>
        <select
          className="field__input"
          id={horizonId}
          value={horizon}
          onChange={(event) => setHorizon(event.target.value)}
          disabled={frozen}
        >
          {HORIZON_CHOICE.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
          {/* A horizon this build has no words for is still the item's, so it stays selectable
              rather than silently becoming *Today* the moment somebody saves an unrelated
              correction. It is shown as what the server sent, which is the honest answer. */}
          {isHorizon(item.timeHorizon) ? null : (
            <option value={item.timeHorizon}>{item.timeHorizon}</option>
          )}
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={dueId}>
          Due by (if there's a date)
        </label>
        <input
          className="field__input"
          id={dueId}
          type="date"
          value={due}
          onChange={(event) => setDue(event.target.value)}
          disabled={frozen}
        />
        <p className="field__hint">
          Leave it empty if there isn't one. Boone treats a date as a nudge, not a deadline.
        </p>
      </div>

      {error === null ? null : (
        <p className="factform__error" role="alert">
          {error}
        </p>
      )}

      <div className="factform__actions">
        <button className="cta" type="submit" disabled={!ready || frozen}>
          {pending ? "Saving…" : "Save the change"}
        </button>
        <button className="cta cta--quiet" type="button" onClick={onCancel} disabled={frozen}>
          Cancel
        </button>
        {/* The canvas's own sentence for this act, said where somebody is doing it. It is the
            one thing about editing that is not obvious and matters most: correcting what Boone
            wrote down never rewrites what the family said. */}
        <span className="factform__because">
          The words you said stay underneath, unchanged — this only corrects what Boone wrote down.
        </span>
      </div>
    </form>
  );
}

/** `INPUT_LIMITS.title`, which the tool enforces server-side. */
const TITLE_LIMIT = 200;

/**
 * The patch: **only what changed**, with retractions in `clear`.
 *
 * Built on every render rather than on submit, because it is also what decides whether *Save
 * the change* is live — a form somebody opened and did not alter should not be able to send an
 * `update` with no fields, which the tool refuses anyway (`update requires at least one of…`)
 * and which would reach the family as a schema error about a body the panel built.
 */
function buildPatch(
  item: PlateItem,
  values: {
    title: string;
    horizon: string;
    due: string;
    owner: string;
    members: readonly RosterMember[];
  },
): ItemPatch {
  const clear: ("dueDate" | "ownerMemberId")[] = [];
  const patch: {
    title?: string;
    timeHorizon?: string;
    dueDate?: string;
    ownerMemberId?: string;
    clear?: ("dueDate" | "ownerMemberId")[];
  } = {};

  if (values.title !== item.title) patch.title = values.title;
  if (values.horizon !== item.timeHorizon) patch.timeHorizon = values.horizon;

  const wasDue = item.dueDate ?? "";
  if (values.due !== wasDue) {
    if (values.due === "") clear.push("dueDate");
    else patch.dueDate = values.due;
  }

  // The owner the form *started* on, not the one on the record: a departed owner opens on
  // *Nobody yet* (see `initialOwner`), and saving an unrelated title fix must not silently
  // send a retraction nobody asked for. Comparing against what was on screen is what makes
  // "only what changed" true from the family's point of view rather than the row's.
  const wasOwner = initialOwner(item, values.members);
  if (values.owner !== wasOwner) {
    if (values.owner === "") clear.push("ownerMemberId");
    else patch.ownerMemberId = values.owner;
  }

  if (clear.length > 0) patch.clear = clear;
  return patch;
}

/**
 * What the owner picker starts on.
 *
 * The member on the record, when the roster still has them. A departed owner starts on
 * *Nobody yet* — `fetchRoster` is active-only, so their id matches no `<option>` and a
 * controlled `<select>` whose value matches nothing renders *blank*, which reads as broken.
 * It is also what the row above already says in words: no owner.
 */
function initialOwner(item: PlateItem, members: readonly RosterMember[]): string {
  const id = item.ownerId;
  return id !== null && members.some((member) => member.userId === id) ? id : "";
}
